import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Extract a user-friendly error message from API error responses.
 * Accepts either:
 *  - the full response body (new envelope: `{ detail, request_id, hint }`),
 *  - the value of `response.data.detail` (legacy + Pydantic),
 *  - a Pydantic validation error array,
 *  - a plain string.
 *
 * @param {any} input - response.data (preferred) or response.data.detail
 * @param {string} fallback - Default message if extraction fails
 * @returns {string} A human-readable error message
 */
export function getErrorMessage(input, fallback = "An error occurred") {
  if (!input) return fallback;

  // New envelope from the FastAPI global handler: { detail, request_id, hint }
  // Prefer `hint` when present (it's the targeted user-facing copy), fall
  // back to `detail` (carries the user-facing message for 4xx; generic for 5xx).
  if (typeof input === "object" && !Array.isArray(input) && (input.detail || input.hint)) {
    return input.hint || (typeof input.detail === "string" ? input.detail : getErrorMessage(input.detail, fallback));
  }

  // Handle array of Pydantic validation errors
  if (Array.isArray(input)) {
    const messages = input.map(err => {
      if (typeof err === 'string') return err;
      // Pydantic error format: { type, loc, msg, input, ctx }
      return err.msg || err.message || 'Validation error';
    });
    return messages.slice(0, 3).join(', ') + (messages.length > 3 ? '...' : '');
  }

  // Handle single object error
  if (typeof input === 'object' && input !== null) {
    return input.msg || input.message || input.error || fallback;
  }

  // Handle string error
  if (typeof input === 'string') {
    return input;
  }

  return fallback;
}
